import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'
import type { Pose } from '../physics/skier'
import { clamp, clamp01, damp } from '../core/math'
import { box, cyl, mergeGeometries, ski, sph } from './geometryUtils'

/**
 * The rider — an articulated figure, posed procedurally from the sim.
 *
 * Realism here is almost entirely *pose*, not polygons. The things that make a
 * silhouette read as a skier rather than a mannequin on rails, each driven by
 * real state:
 *
 *   - **independent legs with true knee flexion.** A two-link solve keeps the
 *     boots planted on the skis at every stance height, so crouching is bent
 *     knees, not a shrinking body. The inside leg of a carve flexes deeper —
 *     the angulated stance that is the sport's most recognisable shape.
 *   - **hip angulation with a counter-rotated torso.** The hips bank with the
 *     skis; the chest banks less and turns against the rotation; the head
 *     stays quietest of all and looks where the *velocity* points, not where
 *     the skis do — so a drift visibly reads in the body before the line.
 *   - **ski lead.** The inside ski runs ahead through a carve and the lead
 *     hands over at the edge change, which is what makes turns look linked.
 *   - **a real tuck** — chest to the thighs, poles trailing under the arms —
 *     rather than a uniform crouch.
 *   - **landing absorption.** Touchdown spikes the flexion and recovers over a
 *     few tenths, scaled by how hard the landing actually was.
 *   - **pole plants** on edge change; **flail** when the sim says sketchy;
 *     a progressive **tumble** with sprawled limbs through a crash.
 *
 * Eleven meshes per rider (two skis, pelvis, two thighs, two shins, chest,
 * head, two arm-and-pole units) sharing one material — cheap enough for the
 * player and both ghosts on a phone.
 */

const KIT = [0.85, 0.27, 0.18] as const
const KIT_DARK = [0.62, 0.19, 0.13] as const
const PANT = [0.16, 0.18, 0.22] as const
const DARK = [0.14, 0.16, 0.19] as const
const SKIN = [0.89, 0.69, 0.53] as const
const SKI_TOP = [0.11, 0.13, 0.16] as const
const PACK = [0.17, 0.24, 0.31] as const
const GOGGLE = [0.20, 0.62, 0.76] as const
const POLE = [0.55, 0.57, 0.6] as const

/** Leg segment lengths, metres. Hip pivot to knee, knee to ankle. */
const THIGH = 0.42
const SHIN = 0.4
/** Ankle height above the ski when standing on it. */
const ANKLE = 0.1
/** Half the ski's length, for normalising the flex curve. */
const SKI_HALF = 0.87

interface SkiFlex {
  /** Reverse camber: the ski bows under load and both ends lift. */
  camber: { value: number }
  /** Shovel deflection over compressions and on touchdown. */
  tip: { value: number }
  /** Edge chatter amplitude on hard snow. */
  chatter: { value: number }
  /** Render-time phase for the chatter. Not simulation state. */
  phase: { value: number }
}

/**
 * Ski flex, in the vertex shader.
 *
 * A ski bending is the most recognisable thing a ski does, and doing it in the
 * shader is free here: each ski is already its own mesh, so a per-ski material
 * costs no extra draw call, and a continuous bend looks far better than
 * chopping the geometry into hinged segments.
 */
function makeSkiMaterial(base: THREE.MeshStandardMaterial): {
  material: THREE.MeshStandardMaterial
  flex: SkiFlex
} {
  const material = base.clone()
  const flex: SkiFlex = {
    camber: { value: 0 }, tip: { value: 0 }, chatter: { value: 0 }, phase: { value: 0 },
  }
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uCamber = flex.camber
    shader.uniforms.uTip = flex.tip
    shader.uniforms.uChatter = flex.chatter
    shader.uniforms.uPhase = flex.phase
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         uniform float uCamber;
         uniform float uTip;
         uniform float uChatter;
         uniform float uPhase;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
         {
           float t = transformed.z / ${SKI_HALF.toFixed(2)};
           // Reverse camber: loaded in a carve, the ski bows and both ends
           // lift away from the binding.
           transformed.y += uCamber * t * t * 0.085;
           // The shovel is softer than the tail, so it deflects further.
           float f = max(t, 0.0);
           transformed.y += uTip * f * f * 0.16;
           // Chatter runs along the length rather than shaking the whole ski,
           // which is what an edge skipping on hard snow actually looks like.
           transformed.y += uChatter * sin(t * 7.0 + uPhase) * 0.014;
         }`,
      )
  }
  // Same generated program for every ski, so they still share one compile.
  material.customProgramCacheKey = () => 'fall-line-ski-flex'
  // Exposed so the flex can be measured from outside rather than squinted at:
  // the uniforms live in a closure and a shader that compiles is not the same
  // as a shader that is being driven.
  material.userData.flex = flex
  return { material, flex }
}

/** Everything the pose reads from the sim, beyond the Pose itself. */
export interface RiderState {
  airborne: boolean
  crashing: boolean
  tucking: boolean
  braking: boolean
  /** 0..1 — the sim's sketch state, drives the flail. */
  sketchy: number
  /** Signed angle from the skis' heading to the velocity, radians. */
  driftYaw: number
  /** Severity of the last landing, 0..1, from the sim. */
  landSeverity: number
  /**
   * The sim's pop cooldown timer. The rig edge-detects a pop from this
   * *rising* — it only ever decays otherwise — which means the extension
   * animation works for the player and both ghosts with no bookkeeping at
   * the call site and nothing new crossing the physics boundary.
   */
  popCooldown: number
  /** 0..1, how hard the skis are working — drives reverse camber. */
  load: number
  /** 0..1, surface hardness — ice chatters, powder does not. */
  hardness: number
  speed: number
}

export class SkierMesh {
  readonly root = new THREE.Group()

  private readonly skiL = new THREE.Group()
  private readonly skiR = new THREE.Group()
  private readonly hips = new THREE.Group()
  private readonly legL = new THREE.Group()
  private readonly legR = new THREE.Group()
  private readonly kneeL = new THREE.Group()
  private readonly kneeR = new THREE.Group()
  private readonly torso = new THREE.Group()
  private readonly head = new THREE.Group()
  private readonly armL = new THREE.Group()
  private readonly armR = new THREE.Group()

  private readonly up = new THREE.Vector3(0, 1, 0)
  private readonly fwd = new THREE.Vector3(0, 0, 1)
  private readonly right = new THREE.Vector3(1, 0, 0)
  private readonly basis = new THREE.Matrix4()

  // Damped pose channels — every one converges toward a target each frame, so
  // states blend into each other instead of snapping.
  private crouch = 0.24
  private absorb = 0
  private landSpike = 0
  private tuck = 0
  private tumble = 0
  private poleSwing = 0
  private plantSide = 1
  private lastRollSign = 0
  private lean = 0
  private drift = 0
  private flailT = 0
  private wasAirborne = false
  private popT = 0
  private lastPopCooldown = 0
  private chatterPhase = 0
  private flexL!: SkiFlex
  private flexR!: SkiFlex

  constructor(private readonly hf: Heightfield, private readonly ghost?: number) {
    this.root.matrixAutoUpdate = false

    const material = this.ghost !== undefined
      ? new THREE.MeshStandardMaterial({
          color: this.ghost, vertexColors: false, roughness: 0.6, metalness: 0,
          transparent: true, opacity: 0.34, depthWrite: false,
        })
      : new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0.05 })
    const mesh = (g: THREE.BufferGeometry): THREE.Mesh => new THREE.Mesh(g, material)

    // --- Skis, one each so the lead can change and each can flex ----------
    const left = makeSkiMaterial(material)
    const right = makeSkiMaterial(material)
    this.flexL = left.flex
    this.flexR = right.flex
    for (const [group, side, skiMat] of [
      [this.skiL, -1, left.material], [this.skiR, 1, right.material],
    ] as const) {
      group.add(new THREE.Mesh(mergeGeometries([
        ski(1.74, 0.115, SKI_TOP),
        box(0.11, 0.05, 0.3, 0, 0.055, 0.02, DARK), // binding
      ]), skiMat))
      group.position.x = side * 0.19
      this.root.add(group)
    }

    // --- Legs: hip pivot -> thigh -> knee pivot -> shin + boot -------------
    for (const [leg, knee, side] of [
      [this.legL, this.kneeL, -1], [this.legR, this.kneeR, 1],
    ] as const) {
      leg.add(mesh(cyl(0.075, 0.062, THIGH, 0, -THIGH / 2, 0, PANT)))
      knee.position.y = -THIGH
      knee.add(mesh(mergeGeometries([
        sph(0.07, 0, 0, 0, PANT),                                  // knee cap
        cyl(0.058, 0.05, SHIN, 0, -SHIN / 2, 0, PANT),
        box(0.13, 0.17, 0.32, 0, -SHIN + 0.02, 0.05, DARK),        // boot
        box(0.11, 0.05, 0.1, 0, -SHIN + 0.13, -0.1, KIT_DARK),     // cuff
      ])))
      leg.add(knee)
      leg.position.set(side * 0.155, -0.02, 0)
      this.hips.add(leg)
    }

    // --- Pelvis ------------------------------------------------------------
    this.hips.add(mesh(mergeGeometries([
      cyl(0.155, 0.17, 0.2, 0, 0.05, 0, PANT, undefined, 8),
      box(0.34, 0.07, 0.24, 0, 0.14, 0, KIT_DARK), // jacket hem
    ])))
    this.root.add(this.hips)

    // --- Torso, head, arms -------------------------------------------------
    this.torso.position.y = 0.16
    this.torso.add(mesh(mergeGeometries([
      cyl(0.19, 0.16, 0.44, 0, 0.24, 0.01, KIT, undefined, 8),     // chest
      sph(0.185, 0, 0.45, 0, KIT, { y: 0.6 }),                     // shoulders
      box(0.3, 0.34, 0.14, 0, 0.26, -0.2, PACK),                   // pack
      box(0.05, 0.26, 0.04, 0.1, 0.28, 0.16, KIT_DARK),            // zip
      box(0.05, 0.26, 0.04, -0.1, 0.28, 0.16, KIT_DARK),
    ])))
    this.hips.add(this.torso)

    this.head.position.y = 0.52
    this.head.add(mesh(mergeGeometries([
      cyl(0.05, 0.06, 0.08, 0, 0.03, 0, SKIN, undefined, 6),       // neck
      sph(0.105, 0, 0.13, 0.01, SKIN),                             // face
      sph(0.125, 0, 0.155, -0.015, DARK, { y: 0.92 }),             // helmet
      box(0.19, 0.075, 0.05, 0, 0.13, 0.1, GOGGLE),                // goggles
    ])))
    this.torso.add(this.head)

    // Arms are one merged unit each, pre-bent ~40 degrees at the elbow, with
    // the pole in the glove. The shoulder pivot does the acting.
    for (const [arm, side] of [[this.armL, -1], [this.armR, 1]] as const) {
      arm.position.set(side * 0.24, 0.42, 0.02)
      arm.add(mesh(mergeGeometries([
        sph(0.075, 0, 0, 0, KIT),                                            // shoulder
        cyl(0.055, 0.048, 0.3, 0, -0.16, 0.02, KIT, { x: -0.28 }),           // upper arm
        cyl(0.048, 0.042, 0.27, 0, -0.38, 0.15, KIT_DARK, { x: -0.85 }),     // forearm
        box(0.09, 0.09, 0.12, 0, -0.46, 0.28, DARK),                         // glove
        cyl(0.011, 0.011, 1.15, 0, -0.75, 0.02, POLE, { x: 0.5 }),           // pole
        cyl(0.05, 0.05, 0.015, 0, -1.24, -0.24, DARK, { x: 0.5 }),           // basket
      ])))
      this.torso.add(arm)
    }
  }

  addTo(scene: THREE.Scene): void {
    scene.add(this.root)
    this.root.traverse((o) => {
      const m = o as THREE.Mesh
      if (!m.isMesh) return
      m.castShadow = true
      m.receiveShadow = this.ghost === undefined
    })
  }

  setVisible(v: boolean): void {
    this.root.visible = v
  }

  update(
    pose: Pose,
    groundNormal: { x: number; y: number; z: number },
    state: RiderState,
    dt: number,
  ): void {
    // --- Root basis: up blends to the slope on the ground, to world in air --
    const tx = state.airborne ? 0 : groundNormal.x
    const ty = state.airborne ? 1 : groundNormal.y
    const tz = state.airborne ? 0 : groundNormal.z
    const k = 1 - Math.exp(-(state.airborne ? 4 : 12) * dt)
    this.up.x += (tx - this.up.x) * k
    this.up.y += (ty - this.up.y) * k
    this.up.z += (tz - this.up.z) * k
    this.up.normalize()

    this.fwd.set(Math.sin(pose.heading), 0, Math.cos(pose.heading))
    this.right.crossVectors(this.fwd, this.up).normalize()
    this.fwd.crossVectors(this.up, this.right).normalize()
    this.right.negate()

    this.basis.makeBasis(this.right, this.up, this.fwd)
    this.basis.setPosition(pose.x, pose.y, pose.z)
    this.root.matrix.copy(this.basis)
    this.root.matrixWorldNeedsUpdate = true

    // --- Channels -----------------------------------------------------------
    const roll = pose.roll
    const rollMag = Math.abs(roll)

    // Touchdown spike, scaled by how hard the sim says the landing was.
    if (this.wasAirborne && !state.airborne) {
      this.landSpike = clamp01(state.landSeverity)
    }
    this.wasAirborne = state.airborne
    this.landSpike = damp(this.landSpike, 0, 4.5, dt)

    // Terrain absorption, as before, plus the landing spike.
    const curvature = this.hf.sampleConcavity(pose.x, pose.z)
    const targetAbsorb = state.airborne ? -0.3 : clamp(curvature * 9, -0.5, 1)
    this.absorb = damp(this.absorb, targetAbsorb, 9, dt)

    this.tuck = damp(this.tuck, state.tucking && !state.crashing ? 1 : 0, 7, dt)
    const targetCrouch =
      state.crashing ? 0.85
      : state.airborne ? 0.55
      : 0.24 + this.tuck * 0.5 + clamp01(this.absorb) * 0.3 + this.landSpike * 0.55
        + (state.braking ? 0.12 : 0)
    this.crouch = damp(this.crouch, clamp(targetCrouch, 0, 0.95), 8, dt)

    this.tumble = state.crashing ? this.tumble + dt * 7.5 : damp(this.tumble, 0, 6, dt)
    this.drift = damp(this.drift, clamp(state.driftYaw, -1.1, 1.1), 8, dt)
    this.lean = damp(this.lean, pose.pitch, 6, dt)
    this.flailT += dt * (1 + state.sketchy * 16)

    // Pop: the cooldown only ever decays, so a rise means one just fired.
    if (state.popCooldown > this.lastPopCooldown + 1e-6) this.popT = 1
    this.lastPopCooldown = state.popCooldown
    this.popT = damp(this.popT, 0, 4.5, dt)

    const rollSign = Math.sign(roll)

    // --- Ski flex -----------------------------------------------------------
    // Reverse camber tracks how hard the skis are working; the shovel takes
    // the terrain (compressions deflect it up, rollovers let it drop) plus the
    // landing spike. Chatter is an edge skipping on hard snow, so it needs
    // both a hard surface and drift to show at all.
    const fast = clamp01(state.speed / 18)
    const camber = clamp01(state.load * 0.8 + rollMag * 0.5) * fast
    const tipTarget =
      clamp(curvature * 7, -0.6, 1) * fast + this.landSpike * 0.9 - this.popT * 0.4
    const chatter = clamp01(Math.abs(this.drift) * 1.6) * state.hardness * fast
    this.chatterPhase += dt * 58

    for (const [flex, sign] of [[this.flexL, -1], [this.flexR, 1]] as const) {
      // The outside ski of a carve carries most of the load, so it bows more.
      const bias = rollSign === 0 ? 1 : (sign === -rollSign ? 1.15 : 0.6)
      flex.camber.value = camber * bias
      flex.tip.value = tipTarget
      flex.chatter.value = chatter
      // Half a cycle apart, so the two skis never buzz in lockstep.
      flex.phase.value = this.chatterPhase + (sign > 0 ? 3.1 : 0)
    }

    // Pole plant on edge change.
    if (rollSign !== 0 && rollSign !== this.lastRollSign && rollMag > 0.12) {
      this.poleSwing = 1
      this.plantSide = rollSign
      this.lastRollSign = rollSign
    }
    this.poleSwing = damp(this.poleSwing, 0, 5, dt)

    // --- Legs: two-link flexion keeps the boots on the skis -----------------
    // Stance height from crouch; each leg then flexes around it. The inside
    // leg of a carve bends deeper — angulation — and the outside leg drives.
    // A pop drives the legs straight — that is where the impulse comes from,
    // and an extension that does not read is a jump that looks like the
    // ground let go rather than the rider pushing off it.
    const flexBase = 0.28 + this.crouch * 0.85 - this.popT * 0.5
    const inside = rollSign // +1 = right leg is inside the turn
    const asym = rollMag * 0.5
    const flexL = clamp(flexBase + (inside < 0 ? asym : -asym * 0.4), 0.06, 1.35)
    const flexR = clamp(flexBase + (inside > 0 ? asym : -asym * 0.4), 0.06, 1.35)

    // Hip height that keeps the *outside* (straighter) leg's boot planted.
    const flexOut = Math.min(flexL, flexR)
    const hipY = ANKLE + (THIGH + SHIN) * Math.cos(flexOut) * 0.985

    this.hips.position.set(roll * -0.1, hipY, -0.02 - this.crouch * 0.1)
    this.legL.rotation.x = -flexL
    this.kneeL.rotation.x = 2 * flexL
    this.legR.rotation.x = -flexR
    this.kneeR.rotation.x = 2 * flexR

    // In the air the legs tuck up symmetrically instead of reaching.
    if (state.airborne) {
      this.legL.rotation.x = -flexL * 1.15
      this.legR.rotation.x = -flexR * 1.15
    }

    // --- Skis: bank with the edge, inside ski leads -------------------------
    const lead = rollSign === 0 ? 0 : 0.13
    this.skiL.position.z = inside < 0 ? lead : -0.03
    this.skiR.position.z = inside > 0 ? lead : -0.03
    this.skiL.rotation.set(0, this.drift * 0.08, roll)
    this.skiR.rotation.set(0, this.drift * 0.08, roll)
    // Airborne the skis hang with the feet.
    const skiY = state.airborne ? 0.16 * this.crouch : 0
    this.skiL.position.y = skiY
    this.skiR.position.y = skiY

    // --- Hips bank with the skis; torso banks less and counters -------------
    this.hips.rotation.set(this.lean * 0.35, 0, roll * 0.8)

    const tuckPitch = this.tuck * 1.05
    const brakePitch = state.braking && !state.tucking ? -0.12 : 0
    this.torso.rotation.set(
      this.lean * 0.5 + tuckPitch + brakePitch + clamp01(this.absorb) * 0.25 + this.landSpike * 0.4,
      -this.drift * 0.35 - roll * 0.22,
      roll * -0.42,
    )

    // Head: quietest of all. Looks along the velocity, pitches up out of the
    // tuck so the eyes stay on the fall line.
    this.head.rotation.set(
      -tuckPitch * 0.85 - this.lean * 0.35,
      this.drift * 0.55 - roll * 0.1,
      roll * -0.18,
    )

    // --- Arms ----------------------------------------------------------------
    // Ride: forward and a little wide. Tuck: swept back along the body.
    // Brake/sketch: thrown wide. Plant: the inside arm punches forward-down.
    const wide = (state.braking ? 0.45 : 0.18) + state.sketchy * 0.5
    const flail = state.sketchy > 0.02
      ? Math.sin(this.flailT * 2.2) * 0.35 * state.sketchy
      : 0
    const airLift = state.airborne ? 0.35 : 0

    for (const [arm, side] of [[this.armL, -1], [this.armR, 1]] as const) {
      const plant = this.plantSide === side ? this.poleSwing : 0
      arm.rotation.set(
        // Arms inherit the torso pitch (they are its children), so the tuck
        // needs only a trim here — 1.15 double-counted the pitch and sent the
        // poles up out of the back like antennas.
        // The pop punches the arms up, which is where the rest of the impulse
        // visually comes from.
        -0.35 - plant * 0.75 + this.tuck * 0.3 - airLift - this.popT * 0.7
          + (side === 1 ? flail : -flail) * 0.4,
        side * this.tuck * 0.25,
        side * (wide + airLift * 0.6 + this.popT * 0.3) + side * this.tuck * -0.28 + flail,
      )
    }

    // --- Crash: progressive tumble, limbs asymmetric and loose --------------
    if (state.crashing) {
      this.hips.rotation.z += this.tumble
      this.hips.rotation.x -= this.tumble * 0.45
      this.torso.rotation.z += this.tumble * 0.5
      this.armL.rotation.z -= 1.1
      this.armR.rotation.z += 1.3
      this.armL.rotation.x = -1.2
      this.armR.rotation.x = -0.4
      this.legL.rotation.x = -0.4
      this.legR.rotation.x = -1.1
      this.skiL.rotation.z += this.tumble * 0.6
      this.skiR.rotation.z += this.tumble * 0.75
      this.hips.position.y = hipY * 0.55
    }
  }
}
